import { FC, PropsWithChildren } from 'react'

import * as RadixTabs from '@radix-ui/react-tabs'
import { styled } from 'styled-components'

import { onSurface, onSurfaceVariant, outline, primary, surfaceBright, surfaceDim } from 'core/styles'

export type TabsProps = PropsWithChildren<{
  defaultValue?: string
  value?: string
  onValueChange?: (value: string) => void
  className?: string
}>

export type TabsListProps = PropsWithChildren<{
  className?: string
}>

export type TabsTriggerProps = PropsWithChildren<{
  value: string
  className?: string
  disabled?: boolean
  asChild?: boolean
}>

export type TabsContentProps = PropsWithChildren<{
  value: string
  className?: string
}>

const TabsRoot: FC<TabsProps> = ({ defaultValue, value, onValueChange, children, className }) => (
  <StyledRoot className={className} defaultValue={defaultValue} onValueChange={onValueChange} value={value}>
    {children}
  </StyledRoot>
)

const TabsList: FC<TabsListProps> = ({ children, className }) => (
  <StyledList className={className}>{children}</StyledList>
)

const TabsTrigger: FC<TabsTriggerProps> = ({ value, children, className, disabled, asChild }) => (
  <StyledTrigger asChild={asChild} className={className} disabled={disabled} value={value}>
    {children}
  </StyledTrigger>
)

const TabsContent: FC<TabsContentProps> = ({ value, children, className }) => (
  <StyledContent className={className} value={value}>
    {children}
  </StyledContent>
)

const Tabs = {
  Root: TabsRoot,
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
}

export default Tabs

const StyledRoot = styled(RadixTabs.Root)`
  width: 100%;
`

const StyledList = styled(RadixTabs.List)`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${outline};
  border-radius: 8px;
  background: ${surfaceDim};
  padding: 1px;
`

const StyledTrigger = styled(RadixTabs.Trigger)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;
  border: 0.5px solid transparent;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  padding: 6px 8px;
  white-space: nowrap;
  color: black;
  font-size: 13px;
  font-weight: 500;

  &:hover:not([data-disabled]) {
    color: ${onSurfaceVariant};
  }

  &:focus-visible {
    outline: 2px solid ${primary};
    outline-offset: 2px;
  }

  &[data-state='active'] {
    border-color: ${outline};
    background-color: ${surfaceBright};
    color: ${onSurface};
  }

  &[data-disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const StyledContent = styled(RadixTabs.Content)`
  padding: 0;

  &:focus-visible {
    outline: 2px solid ${primary};
    outline-offset: 2px;
  }

  &[data-state='inactive'] {
    display: none;
  }
`
